import { inject, Injectable } from '@angular/core';

import { Action, createSelector, Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import { filterBoolean } from '@recruitee/common';
import { DevFlag, DevFlagSelectors } from '@recruitee/dev-flags/data';
import { OfferItem, TalentPool, TalentPoolBrief } from '@recruitee/http/types';
import { memoizeSelector } from '@recruitee/ngrx';
import {
  TalentPoolItemsActions,
  TalentPoolItemsSelectors,
  TalentPoolsBriefActions,
  TalentPoolsBriefSelectors,
} from '@recruitee/offers/data';

import {
  TalentPoolsNewBriefActions,
  TalentPoolsNewBriefSelectors,
} from './talent-pool/talent-pool-brief.store';
import { TalentPoolsNewActions, TalentPoolsNewSelectors } from './talent-pool/talent-pool.store';
import { TalentPoolsScope } from './talent-pool/talent-pool.types';

const featureFlag = DevFlagSelectors.isEnabled(DevFlag.NewTalentPools);
const toNewTypeMap = (oldEntity: OfferItem) =>
  <TalentPool>{
    ...oldEntity,
    department: oldEntity.department || null,
    example: oldEntity.example || false,
  };

const getScope = (archived = false) =>
  archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default;

const oldEntities = createSelector(TalentPoolItemsSelectors.allEntities, entities =>
  entities.map(toNewTypeMap),
);

const collection = createSelector(
  featureFlag,
  oldEntities,
  TalentPoolsNewSelectors.allEntities,
  (flag, oldEntities, newEntities) =>
    (flag ? newEntities : oldEntities).sort((a, b) => b.position - a.position),
);
const collectionScoped = memoizeSelector((archived: boolean = false) =>
  createSelector(
    featureFlag,
    TalentPoolItemsSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    TalentPoolsNewSelectors.entities(archived),
    (flag, oldEntities, newEntities) =>
      (flag ? newEntities : oldEntities.map(toNewTypeMap)).sort((a, b) => b.position - a.position),
  ),
);
const collectionBriefScoped = memoizeSelector((archived: boolean) =>
  createSelector(
    featureFlag,
    TalentPoolsBriefSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    TalentPoolsNewBriefSelectors.entities({
      scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default,
    }),
    (flag, oldEntities, newEntities) =>
      (flag ? newEntities : oldEntities.map(entities => <TalentPoolBrief>entities)).sort(
        (a, b) => b.position - a.position,
      ),
  ),
);
const metaScoped = memoizeSelector((archived = false) =>
  createSelector(
    featureFlag,
    TalentPoolItemsSelectors.meta({ scope: getScope(archived) }),
    TalentPoolsNewSelectors.meta({ scope: getScope(archived) }),
    (flag, oldMeta, newMeta) => (flag ? newMeta : oldMeta),
  ),
);
const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(collection, entities =>
    entities.find(entity => entity.id === identifier || entity.slug === identifier),
  ),
);

const injectFeatureFlag = () => {
  let enabled = false;
  inject(Store)
    .select(DevFlagSelectors.isEnabled(DevFlag.NewTalentPools))
    .pipe(take(1))
    .subscribe(flag => (enabled = flag));
  return enabled;
};

@Injectable({
  providedIn: 'root',
})
export class TalentPoolsDataService {
  private store = inject(Store);
  private newTalentPools = injectFeatureFlag();

  public collection$(archived: boolean = false): Observable<TalentPool[]> {
    return this.store.select(collectionScoped(archived)).pipe(filterBoolean);
  }

  public collectionBrief$(archived: boolean = false): Observable<TalentPoolBrief[]> {
    return this.store.select(collectionBriefScoped(archived)).pipe(filterBoolean);
  }

  public meta$(archived = false) {
    return this.store.select(metaScoped(archived));
  }

  public byIdOrSlugFromCollection$(idOrSlug: number | string) {
    return this.store.select(byIdOrSlug(idOrSlug));
  }

  public fetchCollection(archived: boolean = false) {
    let action: Action;
    if (this.newTalentPools) {
      const scope: TalentPoolsScope = archived
        ? TalentPoolsScope.Archived
        : TalentPoolsScope.Default;
      action = TalentPoolsNewActions.fetchCollection({ payload: { scope } });
    } else {
      action = archived
        ? TalentPoolItemsActions.fetchArchivedCollection()
        : TalentPoolItemsActions.fetchActiveCollection();
    }
    this.store.dispatch(action);
  }

  public fetchBriefCollection() {
    const action = this.newTalentPools
      ? TalentPoolsNewBriefActions.fetchCollection()
      : TalentPoolsBriefActions.fetchCollection();
    this.store.dispatch(action);
  }
}
